// @ts-ignore
import * as mapboxgl from 'mapbox-gl';
import {
  GeoCoords,
  QGraph,
  QGraphConnection,
  QGraphVertex,
  QGraphVertexWithRadius
} from '../interfaces/geo-and-movement.interfaces';
import * as GeoJSON from 'geojson';
import * as turf from '@turf/turf'
import {isStartZoneCircle} from '../interfaces/quest-core.interfaces';
import {nMetersFromCoords} from './geo.functions';
import {Dict} from '../interfaces/common.interfaces';

const ZONE_LINE_WIDTH = 3;

export function addLayersToMap(map: mapboxgl.Map): void {
  map.addSource('start-zones', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: []
    }
  });

  map.addSource('start-zone-outline', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: []
    }
  });

  map.addLayer({
    id: 'start-zones',
    type: 'fill',
    source: 'start-zones',
    layout: {},
    paint: {
      'fill-color': '#34C759',
      'fill-opacity': 0.3
    }
  });

  map.addLayer({
    id: 'start-zone-outline',
    type: 'line',
    source: 'start-zone-outline',
    layout: {},
    paint: {
      'line-color': '#34C759',
      'line-width': ZONE_LINE_WIDTH
    }
  });

  map.addSource('hazard-zones', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: []
    }
  });

  map.addSource('hazard-zones-outline', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: []
    }
  });

  map.addLayer({
    id: 'hazard-zones',
    type: 'fill',
    source: 'hazard-zones',
    layout: {},
    paint: {
      'fill-color': '#FF3B30',
      'fill-opacity': 0.3
    }
  });

  map.addLayer({
    id: 'hazard-zones-outline',
    type: 'line',
    source: 'hazard-zones-outline',
    layout: {},
    paint: {
      'line-color': '#FF3B30',
      'line-width': ZONE_LINE_WIDTH
    }
  });

  map.addSource('task-zone', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: []
    }
  });

  map.addSource('task-zone-outline', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: []
    }
  });

  map.addLayer({
    id: 'task-zone',
    type: 'fill',
    source: 'task-zone',
    layout: {},
    paint: {
      'fill-color': '#F2C94C',
      'fill-opacity': 0.3
    }
  });

  map.addLayer({
    id: 'task-zone-outline',
    type: 'line',
    source: 'task-zone-outline',
    layout: {},
    paint: {
      'line-color': '#F2C94C',
      'line-width': 2
    }
  });

  map.addSource('discovery-zones', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: []
    }
  });

  map.addLayer({
    id: 'discovery-zones',
    type: 'fill',
    source: 'discovery-zones',
    layout: {},
    paint: {
      'fill-color': '#6225ad',
      'fill-opacity': 0.3
    }
  });

  map.addLayer({
    id: 'discovery-outline',
    type: 'line',
    source: 'discovery-zones',
    layout: {},
    paint: {
      'line-color': '#6225ad',
      'line-width': ZONE_LINE_WIDTH
    }
  });
}

export function drawMarkerOnMap(coords: GeoCoords, map: mapboxgl.Map, path = '../../../../assets/map-icons/active-marker-1.svg'): mapboxgl.Marker {
  const el = document.createElement('div');
  el.innerHTML = `<img src="${path}" alt="NO IMG">`
  el.className = 'find-marker';

  return new mapboxgl.Marker(el).setLngLat(coords).addTo(map);
}

export const drawStaticMarker = (
  coords: GeoCoords,
  type: 'revealed' | 'reached' | 'with-text',
  data?: Dict<string | number>,
  onClick?: () => void
): mapboxgl.Marker => {
  let path = '';
  switch (type) {
    case 'revealed':
      path = 'assets/gameplay-icons/revealed-marker.svg';
      break;
    case 'reached':
      path = 'assets/gameplay-icons/reached-marker.svg';
      break;
    case 'with-text':
      path = 'assets/gameplay-icons/revealed-marker-empty.svg';
      break;
    default:
      throw Error('Unknown marker type ' + type);
  }
  const el = document.createElement('div');
  el.innerHTML = `<img src="${path}" alt="NO IMG">`;
  if (type === 'with-text') {
    let cssClass =  data?.['text'] === '!' ? 'exclamation-mark' : 'marker-number'
    if (data?.['text']?.toString().length > 1) {
      cssClass += ' two-digits';
    }
    el.innerHTML += `<span class="${cssClass}">${data?.['text']}</span>`;
  }
  el.className = 'map-marker '  + (type === 'reached' ? 'reached' : '');

  if (onClick) {
    el.style.cursor = 'pointer';
  }

  const marker = new mapboxgl.Marker(el).setLngLat(coords);

  if (onClick) {
    el.addEventListener('click', (ev: any) => {
      onClick();

      ev.stopPropagation();
    });
  }

  return marker;
};

export const drawCommentOnMap = (coords: GeoCoords, map: mapboxgl.Map, active = false): {
  popup: mapboxgl.Popup,
  updateComment: (comment: string, imgUrl?: string, active?: boolean) => void
} => {
  let comment = '';
  let actualComment = '';
  let imageUrl = '';

  const popup = new mapboxgl.Popup({
    closeButton: false,
    closeOnClick: false
  })
    .setLngLat([coords.lng, coords.lat])
    .setHTML(`<div class="regular-14 comment-on-map-content"></div>`)
    .addTo(map);

  const updateComment = (c: string, u?: string, a?: boolean) => {
    if (a !== undefined) {
      active = a;
    }

    if (!c) {
      c = '';
    }

    comment = c;
    actualComment = active ? c : (c.length > 20 ? c.slice(0, 20) + '...' : c);
    imageUrl = u;

    popup.setHTML(`
<div class="regular-14 comment-on-map-content ${active ? '' : 'unfocused'}">
${u ? `<img src="${u}" alt="NO IMG">` : ''}
${actualComment}
</div>`);
  }

  popup.getElement().addEventListener('mouseenter', () => {
    active = true;

    updateComment(comment, imageUrl);
  });

  popup.getElement().addEventListener('mouseleave', () => {
    active = false;

    updateComment(comment, imageUrl);
  });

  return {
    popup,
    updateComment
  };
}

export function zoneToFeatureLineString(zone: QGraph | QGraphVertexWithRadius, closeZone = false): GeoJSON.Feature {
  let coordinates: GeoJSON.Position[] = []

  if (isStartZoneCircle(zone)) {
    return turf.circle([zone.coords.lng, zone.coords.lat], zone.data.radius, {steps: 60, units: 'meters'});
  } else {
    if (zone.connections?.length) {
      const connection = zone.connections![0];
      const firstVertex = zone.vertices[connection.startVertexId];
      const firstCoords = [firstVertex.coords.lng, firstVertex.coords.lat];

      coordinates = [firstCoords, ...zone.connections!.map(c => {
        const vertex = zone.vertices[c.endVertexId];
        return [vertex.coords.lng, vertex.coords.lat];
      })];

      if (closeZone) {
        coordinates.push(firstCoords);
      }
    }

    return {
      type: 'Feature',
      properties: null,
      geometry: {
        type: 'LineString',
        coordinates
      }
    } as GeoJSON.Feature;
  }
  //not sure if I want to use turf here an obfuscate GeoJSON
  // turf.lineString([firstCoords, ...zone.connections!.map(c => {
  //   const vertex = zone.vertices[c.endVertexId];
  //   return [vertex.coords.lng, vertex.coords.lat];
  // })])
}

export function zoneToFeaturePolygon(zone: QGraph | QGraphVertexWithRadius): GeoJSON.Feature {
  let coordinates: GeoJSON.Position[][] = []

  if (isStartZoneCircle(zone)) {
    return turf.circle([zone.coords.lng, zone.coords.lat], zone.data.radius, {steps: 60, units: 'meters'});
  }

  if (zone.connections?.length && zone.connections?.length >= 2) {
    const connection = zone.connections![0];
    const firstVertex = zone.vertices[connection.startVertexId];
    const firstCoords = [firstVertex.coords.lng, firstVertex.coords.lat];

    coordinates = [[firstCoords, ...zone.connections!.map(c => {
      const vertex = zone.vertices[c.endVertexId];
      return [vertex.coords.lng, vertex.coords.lat];
    })]];
  }

  return {
    type: 'Feature',
    properties: null,
    geometry: {
      type: 'Polygon',
      coordinates
    }
  } as GeoJSON.Feature;
}

export function getAllCoordinatesFromZone(zone: QGraph | QGraphVertexWithRadius): GeoCoords[] {
  let coords: GeoCoords[] = [];

  if (isStartZoneCircle(zone)) {
    coords = [
      nMetersFromCoords(zone.coords, zone.data.radius, 'n'),
      nMetersFromCoords(zone.coords, zone.data.radius, 'e'),
      nMetersFromCoords(zone.coords, zone.data.radius, 's'),
      nMetersFromCoords(zone.coords, zone.data.radius, 'w')
    ]
  } else {
    for (const [, value] of Object.entries(zone.vertices)) {
      coords.push(value.coords);
    }
  }

  return coords;
}

export function coordsToQGraph(coords: GeoCoords[]): QGraph {
  const vertices: Dict<QGraphVertex> = {};
  const connections: Array<QGraphConnection> = [];

  coords.forEach((c, index) => {
    vertices[index] = {
      id: `${index}`,
      coords: c
    }

    if (index > 0) {
      connections.push({
        startVertexId: `${index - 1}`,
        endVertexId: `${index}`
      })
    }
  });

  return {
    vertices,
    connections
  }
}

export const adjustMapBBox = (coords: GeoCoords[], map: mapboxgl.Map, bottomOffset = 0, leftOffset = 0, padding = 70): void => {
  if (coords.length > 1) {
    const mapHeight = map.getContainer().clientHeight;

    const percentCoveredByModal = bottomOffset / mapHeight;

    const mapWidth = map.getContainer().clientWidth;

    const percentCoveredByLeftOffset = leftOffset / mapWidth;

    const coordinates: [number, number][] = coords.map(v => [v.lng, v.lat]);

    const bounds = new mapboxgl.LngLatBounds(
      coordinates[0],
      coordinates[0]
    );

    for (const coord of coordinates) {
      bounds.extend(coord);
    }

    const distY = bounds.getNorth() - bounds.getSouth();

    // we compensate for the bottom modal height
    bounds.extend([bounds.getEast(), bounds.getSouth() - distY * percentCoveredByModal]);

    const distX = bounds.getEast() - bounds.getWest();

    // we compensate for the left panel height
    bounds.extend([bounds.getWest() - distX * percentCoveredByLeftOffset, bounds.getNorth()]);

    map.fitBounds(bounds, {
      padding
    });
  } else {
    map.setCenter([coords[0].lng, coords[0].lat]);
  }
};

export const getDistanceInPixels = (map: mapboxgl.Map, point1: GeoCoords, point2: GeoCoords): number => {
  // Project geographical coordinates to pixel coordinates
  const pixelCoords1 = map.project(point1);
  const pixelCoords2 = map.project(point2);

  // Calculate the Euclidean distance between the two pixel coordinates
  const dx = pixelCoords2.x - pixelCoords1.x;
  const dy = pixelCoords2.y - pixelCoords1.y;
  return Math.sqrt(dx * dx + dy * dy);
}

export const drawSignMarker = (coords: GeoCoords, name: string): mapboxgl.Marker => {
  const path = `assets/signs-images/${name}.svg`;
  const el = document.createElement('div');
  el.innerHTML = `<img src="${path}" alt="NO IMG">`;
  el.style.width = '30px';
  el.style.height = '72px';

  return new mapboxgl.Marker(el).setLngLat(coords);
}

export const drawImageMarker = (coords: GeoCoords, uri: string, bearing: number, onClick?: () => void): mapboxgl.Marker => {
  console.log('coords and bearing', coords, bearing);

  if (!uri.includes('thumbnails') && uri.includes('obelisk_images')) {
    uri = uri.replace('obelisk_images', 'obelisk_images/thumbnails');
  }

  const el = document.createElement('div');
  el.className = 'user-placed-data-marker'

  el.innerHTML = `
    <div class="marker-body-container">
      <img src="${uri}" alt="">
    </div>
    <div class="pin"></div>
    <div class="bearing-arrow" style="rotate: ${bearing + 180}deg"></div>
  `

  const markerBodyContainer = el.getElementsByClassName('marker-body-container')[0];

  markerBodyContainer.addEventListener('click', () => {
    if (onClick) {
      onClick();
    }
  });

  return new mapboxgl.Marker(el).setLngLat(coords);
}

export const drawCommentMarker = (coords: GeoCoords, onClick?: () => void): mapboxgl.Marker => {
  const el = document.createElement('div');
  el.className = 'user-placed-data-marker'

  el.innerHTML = `
    <div class="marker-body-container centered-content">
      <img style="width: 32px; height: 32px" src="assets/project-icons/message-white.svg" alt="">
    </div>
    <div class="pin"></div>
  `

  const markerBodyContainer = el.getElementsByClassName('marker-body-container')[0];

  markerBodyContainer.addEventListener('click', () => {
    if (onClick) {
      onClick();
    }
  });

  return new mapboxgl.Marker(el).setLngLat(coords);
}


export const drawPathOnMap = (path: GeoCoords[], color: string, map: mapboxgl.Map): void => {
  const line = turf.lineString(path.map(p => [p.lng, p.lat]));

  map.addSource('path', {
    type: 'geojson',
    data: line
  });

  map.addLayer({
    id: 'path',
    type: 'line',
    source: 'path',
    layout: {},
    paint: {
      'line-color': color,
      'line-width': 3
    }
  });
}

export const zoneToLineString = (zone: QGraph, closeZone = false): GeoJSON.LineString => {
  let coordinates: GeoJSON.Position[] = []

  if (zone.connections?.length) {
    const connection = zone.connections![0];
    const firstVertex = zone.vertices[connection.startVertexId];
    const firstCoords = [firstVertex.coords.lng, firstVertex.coords.lat];

    coordinates = [firstCoords, ...zone.connections!.map(c => {
      const vertex = zone.vertices[c.endVertexId];
      return [vertex.coords.lng, vertex.coords.lat];
    })];

    if (closeZone) {
      coordinates.push(firstCoords);
    }
  }

  return {
    type: 'LineString',
    coordinates
  };
}

export const getDefaultLayerNames = (): string[] => ([
  "land",
  "landcover",
  "national-park",
  "national-park_tint-band",
  "landuse",
  "pitch-outline",
  "waterway-shadow",
  "water-shadow",
  "waterway",
  "water",
  "water-depth",
  "wetland",
  "wetland-pattern",
  "hillshade",
  "contour-line",
  "land-structure-polygon",
  "land-structure-line",
  "aeroway-polygon",
  "aeroway-line",
  "building",
  "building-underground",
  "tunnel-minor-case",
  "tunnel-street-case",
  "tunnel-minor-link-case",
  "tunnel-secondary-tertiary-case",
  "tunnel-primary-case",
  "tunnel-major-link-case",
  "tunnel-motorway-trunk-case",
  "tunnel-path-trail",
  "tunnel-path-cycleway-piste",
  "tunnel-path",
  "tunnel-steps",
  "tunnel-pedestrian",
  "tunnel-construction",
  "tunnel-minor",
  "tunnel-minor-link",
  "tunnel-major-link",
  "tunnel-street",
  "tunnel-street-low",
  "tunnel-secondary-tertiary",
  "tunnel-primary",
  "tunnel-motorway-trunk",
  "tunnel-oneway-arrow-blue",
  "tunnel-oneway-arrow-white",
  "cliff",
  "ferry",
  "ferry-auto",
  "road-pedestrian-polygon-fill",
  "road-pedestrian-polygon-pattern",
  "road-path-bg",
  "road-steps-bg",
  "road-pedestrian-case",
  "road-path-trail",
  "road-path-cycleway-piste",
  "road-path",
  "road-steps",
  "road-pedestrian",
  "golf-hole-line",
  "road-polygon",
  "turning-feature-outline",
  "road-minor-case",
  "road-street-case",
  "road-minor-link-case",
  "road-secondary-tertiary-case",
  "road-primary-case",
  "road-major-link-case",
  "road-motorway-trunk-case",
  "turning-feature",
  "road-construction",
  "road-minor",
  "road-minor-link",
  "road-major-link",
  "road-street",
  "road-street-low",
  "road-secondary-tertiary",
  "road-primary",
  "road-motorway-trunk",
  "road-rail",
  "road-rail-tracks",
  "level-crossing",
  "road-oneway-arrow-blue",
  "road-oneway-arrow-white",
  "crosswalks",
  "gate-fence-hedge",
  "bridge-path-bg",
  "bridge-steps-bg",
  "bridge-pedestrian-case",
  "bridge-path-trail",
  "bridge-path-cycleway-piste",
  "bridge-path",
  "bridge-steps",
  "bridge-pedestrian",
  "gate-label",
  "bridge-minor-case",
  "bridge-street-case",
  "bridge-minor-link-case",
  "bridge-secondary-tertiary-case",
  "bridge-primary-case",
  "bridge-major-link-case",
  "bridge-motorway-trunk-case",
  "bridge-construction",
  "bridge-minor",
  "bridge-minor-link",
  "bridge-major-link",
  "bridge-street",
  "bridge-street-low",
  "bridge-secondary-tertiary",
  "bridge-primary",
  "bridge-motorway-trunk",
  "bridge-major-link-2-case",
  "bridge-motorway-trunk-2-case",
  "bridge-major-link-2",
  "bridge-motorway-trunk-2",
  "bridge-oneway-arrow-blue",
  "bridge-oneway-arrow-white",
  "bridge-rail",
  "bridge-rail-tracks",
  "aerialway",
  "admin-1-boundary-bg",
  "admin-0-boundary-bg",
  "admin-1-boundary",
  "admin-0-boundary",
  "admin-0-boundary-disputed",
  "contour-label",
  "building-entrance",
  "building-number-label",
  "block-number-label",
  "road-label",
  "road-intersection",
  "road-number-shield",
  "road-exit-shield",
  "path-pedestrian-label",
  "golf-hole-label",
  "ferry-aerialway-label",
  "waterway-label",
  "natural-line-label",
  "natural-point-label",
  "water-line-label",
  "water-point-label",
  "poi-label",
  "transit-label",
  "airport-label",
  "settlement-subdivision-label",
  "settlement-minor-label",
  "settlement-major-label",
  "state-label",
  "country-label",
  "continent-label",
  "mapbox",
  "background",
  "satellite"
]);

export const getDefaultSourceNames = (): string[] => ([
  'composite',
  'satellite',
  'mapbox-dem',
  'mapbox'
]);
